import React, { useState } from 'react';
import api from '../api/client';
import './UploadOrdersPopup.css';

const CODE = { fontSize: '0.82rem' };

/**
 * ReferenceGuidePopup
 * Справка по загрузке заказов из файла (CSV / XLSX): статусы, поставщики выбранного типа,
 * колонки файлов материалов и семян, валюта / единица / импортные расходы.
 *
 * Props:
 *   materialTypes  [{ materialtypeID, materialName }]
 *   statuses       [{ statusID, status_name }]
 *   onClose
 */
const ReferenceGuidePopup = ({ materialTypes = [], statuses = [], onClose }) => {
  const [selectedTypeID, setSelectedTypeID] = useState('');
  const [guideSuppliers, setGuideSuppliers] = useState([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);

  const handleTypeChange = async (e) => {
    const id = e.target.value;
    setSelectedTypeID(id);
    setGuideSuppliers([]);
    if (!id) return;
    setIsLoadingSuppliers(true);
    try {
      const res = await api.get(id === 'seeds' ? '/api/seeds_suppliers' : `/api/materialsuppliers_for_type/${id}`);
      setGuideSuppliers(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ }
    finally { setIsLoadingSuppliers(false); }
  };

  const selectedTypeName = selectedTypeID === 'seeds'
    ? 'Seeds'
    : materialTypes.find(t => String(t.materialtypeID) === String(selectedTypeID))?.materialName || '';

  return (
    <div className="upload-orders-overlay" onClick={onClose}>
      <div className="upload-orders-modal" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
        <div className="upload-orders-header">
          <h2 className="upload-orders-title">
            <i className="fa-solid fa-circle-info" style={{ marginRight: 8, color: '#6366f1' }}></i>
            Reference Guide — Orders Upload
          </h2>
          <button className="upload-orders-close" onClick={onClose}>×</button>
        </div>
        <div className="upload-orders-body">
          <p style={{ color: '#475569', fontSize: '0.9rem' }}>
            Use the values below exactly as shown when filling your CSV/XLSX file.
          </p>

          {/* Material type selector */}
          <div className="upload-orders-field" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            <label className="upload-orders-label">Select Material Type to see its suppliers:</label>
            <select className="upload-orders-input" value={selectedTypeID} onChange={handleTypeChange} style={{ width: 220 }}>
              <option value="">Choose type...</option>
              {materialTypes.map(t => (
                <option key={t.materialtypeID} value={t.materialtypeID}>{t.materialName}</option>
              ))}
              <option value="seeds">Seeds</option>
            </select>
          </div>

          {/* Statuses */}
          <div>
            <div className="upload-orders-preview-title" style={{ marginBottom: 6 }}>
              <i className="fa-solid fa-tag" style={{ marginRight: 6, color: '#6366f1' }}></i>
              Order Statuses — <code style={CODE}>statusID</code> column
            </div>
            <div className="upload-orders-table-wrapper" style={{ maxHeight: 180 }}>
              <table className="upload-orders-table">
                <thead><tr><th>statusID</th><th>Status Name</th><th>Notes</th></tr></thead>
                <tbody>
                  {statuses.map(s => (
                    <tr key={s.statusID}>
                      <td><code style={{ fontWeight: 700 }}>{s.statusID}</code></td>
                      <td>{s.status_name}</td>
                      <td style={{ color: '#64748b', fontSize: '0.8rem' }}>
                        {s.statusID === 'BO'
                          ? 'Requires bo_follow_up_date; est_del_date must be empty'
                          : 'Requires est_del_date; bo_follow_up_date must be empty'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Suppliers for selected type */}
          {selectedTypeID && (
            <div>
              <div className="upload-orders-preview-title" style={{ marginBottom: 6 }}>
                <i className="fa-solid fa-truck" style={{ marginRight: 6, color: '#6366f1' }}></i>
                Suppliers for <strong>{selectedTypeName}</strong> — <code style={CODE}>supplierName</code> column
              </div>
              <div className="upload-orders-table-wrapper" style={{ maxHeight: 180 }}>
                <table className="upload-orders-table">
                  <thead><tr><th>supplierName (use exactly)</th><th>supplierID</th></tr></thead>
                  <tbody>
                    {isLoadingSuppliers
                      ? <tr><td colSpan={2} style={{ color: '#94a3b8' }}>Loading...</td></tr>
                      : guideSuppliers.length === 0
                        ? <tr><td colSpan={2} style={{ color: '#94a3b8' }}>No suppliers found</td></tr>
                        : guideSuppliers.map(s => (
                          <tr key={s.supplierID}>
                            <td><code style={{ fontWeight: 700 }}>{s.supplierName}</code></td>
                            <td>{s.supplierID}</td>
                          </tr>
                        ))
                    }
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* File columns */}
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '10px 14px', fontSize: '0.85rem', color: '#166534', lineHeight: 1.6 }}>
            <i className="fa-solid fa-lightbulb" style={{ marginRight: 6 }}></i>
            <strong>Materials file</strong> (Add Materials Order)<br />
            Required columns: sku, year, month_num, supplierName, statusID, qty, price_per_pcs<br />
            Optional columns: est_del_date, bo_follow_up_date, tracking, duties<br />
            <strong>Seeds file</strong> (Add Seeds Order)<br />
            Required columns: seedsid, year, month_num, supplierName, statusID, qty, price<br />
            Optional columns: est_del_date, bo_follow_up_date, tracking, duties, lab_testing<br />
            Dates format: <code>YYYY-MM-DD</code>
          </div>

          {/* Currency, unit, import costs */}
          <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6, padding: '10px 14px', fontSize: '0.85rem', color: '#1e40af', lineHeight: 1.6 }}>
            <i className="fa-solid fa-coins" style={{ marginRight: 6 }}></i>
            <strong>Currency, unit and import costs</strong><br />
            Currency (USD or EUR with the EUR→USD rate), unit (lb or kg, seeds only) and Import order
            are chosen in the upload window, once for the whole file.<br />
            Fill <code>qty</code> and the price as on the invoice, in that unit and currency.
            The system converts them to lb and USD itself.<br />
            <code>duties</code> and <code>lab_testing</code> are amounts in USD for the row.
            They count towards COGS when Import order is ticked.<br />
            Customs &amp; Compliance, Freight and Packaging are entered on the order page after the upload.<br />
            Older seeds files with <code>lbqty</code> and <code>price_per_pcs</code> columns still work.
          </div>
        </div>
        <div className="upload-orders-footer">
          <button className="upload-orders-btn upload-orders-btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default ReferenceGuidePopup;
